import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation, useNavigationType } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaExclamationTriangle, FaInfoCircle, FaLink } from 'react-icons/fa';
import ImportListingModal from './ImportListingModal';

const PageContainer = styled.div`
  padding: 40px;
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 24px 12px 32px;
  }
`;

// Botões de ação compactos com ícones
const IconButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #eee;
  background: #fff;
  color: #666;
  cursor: pointer;
  transition: all .2s;
  padding: 0;

  &:hover { background: #f8f9fa; }
  &:disabled { opacity: .5; cursor: not-allowed; }

  &.primary { color: #34574A; border-color: #dfe6e3; }
  &.warn { color: #dc3545; border-color: #f1d6da; }
  &.highlight {
    background: ${props => props.$active ? '#ba824f' : '#fff'};
    color: ${props => props.$active ? '#fff' : '#ba824f'};
    border-color: ${props => props.$active ? '#ba824f' : '#e0b07c'};
  }

  svg { width: 18px; height: 18px; }
`;

// Modal genérico para mensagens informativas
const InfoModal = ({ title = 'Aviso', message, onClose }) => {
  const isError = title.toLowerCase().includes('erro') || title.toLowerCase().includes('limite');
  const Icon = isError ? FaExclamationTriangle : FaInfoCircle;
  return (
    <DeleteModalOverlay>
      <DeleteModal style={{ padding: '34px 32px 28px', alignItems: 'stretch' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 12 }}>
          <Icon style={{ fontSize: 40, color: isError ? '#ba824f' : '#34574A', marginBottom: 14 }} />
          <h3 style={{ margin: 0, color: '#34574A', fontWeight: 700, fontSize: '1.25rem', textAlign: 'center' }}>{title}</h3>
        </div>
        <p style={{ marginBottom: 0, textAlign: 'center', whiteSpace: 'pre-wrap', color: '#555', lineHeight: 1.65, fontSize: '0.98rem' }}>{message}</p>
        <ModalActions style={{ justifyContent: 'center', marginTop: 26 }}>
          <ActionButton highlight onClick={onClose} style={{ padding: '10px 28px', borderRadius: 8, fontWeight: 600 }}>OK</ActionButton>
        </ModalActions>
      </DeleteModal>
    </DeleteModalOverlay>
  );
};

// Modal de confirmação reutilizável
const ConfirmModal = ({ title = 'Confirmação', message, confirmText = 'Confirmar', cancelText = 'Cancelar', onConfirm, onCancel }) => (
  <DeleteModalOverlay>
    <DeleteModal>
      <h3 style={{ marginBottom: 12 }}>{title}</h3>
      <p style={{ marginBottom: 0, textAlign: 'center', whiteSpace: 'pre-wrap' }}>{message}</p>
      <ModalActions>
        <ActionButton onClick={onCancel}>{cancelText}</ActionButton>
        <ActionButton highlight onClick={onConfirm}>{confirmText}</ActionButton>
      </ModalActions>
    </DeleteModal>
  </DeleteModalOverlay>
);

const WelcomeHeader = styled.div`
  background: white;
  padding: 20px 40px;
  border-radius: 12px;
  margin-bottom: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  display: flex;
  justify-content: space-between;
  align-items: center;

  h1 {
    font-size: 1.5rem;
    color: #333;
    font-weight: 500;
  }
`;

const ValidityTag = styled.div`
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 0.72rem;
  font-weight: 500;
  background: rgba(255,255,255,0.92);
  color: #34574A;
  border: 1px solid #e6e6e6;
`;

const ImportButton = styled.button`
  background: #fff;
  color: #34574A;
  border: 1px solid #dfe6e3;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 0.875rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;

  &:hover {
    background: #f3f7f5;
    border-color: #34574A;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const AddButton = styled.button`
  background: #34574A;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 0.875rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.3s ease;

  &:hover {
    background: #2a4539;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const FiltersContainer = styled.div`
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 24px;
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 200px;
  padding: 12px 16px;
  border: 2px solid #eee;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: all 0.3s ease;

  &:focus {
    border-color: #ba824f;
    outline: none;
  }
`;

const FilterSelect = styled.select`
  padding: 12px 16px;
  border: 2px solid #eee;
  border-radius: 8px;
  font-size: 0.875rem;
  background: white;
  cursor: pointer;
  min-width: 150px;

  &:focus {
    border-color: #ba824f;
    outline: none;
  }
`;

const ListingsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  justify-items: stretch;
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const ListingCard = styled.div`
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  box-sizing: border-box;
`;

const TagsOverlay = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  right: 12px;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
  z-index: 2;
  align-items: center;
`;

const ListingImage = styled.div`
  position: relative;
  padding-top: 60%;
  background: ${props => `url(${props.image}) center/cover no-repeat`};
`;

const ListingStatus = styled.div`
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${props => props.$active ? '#e6f4ea' : '#ffebee'};
  color: ${props => props.$active ? '#1e7e34' : '#c62828'};
  margin-left: auto;
  flex-shrink: 0;
  white-space: nowrap;
`;

const HighlightBadge = styled.div`
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  background: #ba824f;
  color: white;
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    width: 14px;
    height: 14px;
    fill: currentColor;
  }
`;

const CategoryTag = styled.div`
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${props => {
    if (props.$category === 'buy') return '#2196F3'; // Blue
    if (props.$category === 'rent') return '#4CAF50'; // Green
    if (props.$category === 'vacation') return '#FF9800'; // Orange
    return '#34574A';
  }};
  color: white;
`;

const ListingContent = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
`;

const ListingTitle = styled.h3`
  font-size: 1rem;
  color: #333;
  margin: 0 0 8px 0;
`;

const CopyTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f3f6ff;
  color: #2f4aa0;
  border: 1px solid #d6e0ff;
  border-radius: 12px;
  padding: 2px 8px;
  font-size: 0.72rem;
  font-weight: 600;
  margin-left: 8px;
`;

const ListingLocation = styled.p`
  font-size: 0.875rem;
  color: #666;
  margin: 0 0 16px 0;
`;

const ListingStats = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #eee;

  .stat {
    text-align: center;
    
    span {
      display: block;
      font-size: 0.875rem;
      color: #333;
      font-weight: 500;
    }
    
    small {
      font-size: 0.75rem;
      color: #666;
    }
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const ListingPrice = styled.div`
  font-size: 1.125rem;
  color: #333;
  font-weight: 500;
  margin-bottom: 0;
  white-space: nowrap;
  flex-shrink: 0;
`;

const ListingActions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;
  flex: 1 1 auto;
`;

const ListingFooter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  padding-top: 20px;
  margin-top: auto;
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 48px;
  height: 24px;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: .4s;
    border-radius: 24px;

    &:before {
      position: absolute;
      content: "";
      height: 16px;
      width: 16px;
      left: 4px;
      bottom: 4px;
      background-color: white;
      transition: .4s;
      border-radius: 50%;
    }
  }

  input:checked + .slider {
    background-color: #34574A;
  }

  input:checked + .slider:before {
    transform: translateX(24px);
  }
`;

const ActionButton = styled.button`
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => props.highlight ? '#ba824f' : 'white'};
  color: ${props => props.highlight ? 'white' : '#666'};
  border: ${props => props.highlight ? 'none' : '1px solid #eee'};
  min-width: 80px;
  max-width: 100px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  flex-shrink: 1;

  &:hover {
    background: ${props => props.highlight ? '#a77445' : '#f8f9fa'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Toast = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  background: #34574A;
  color: white;
  padding: 16px 24px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 12px;
  z-index: 1000;
  animation: slideIn 0.3s ease;

  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  svg {
    width: 20px;
    height: 20px;
    fill: currentColor;
  }
`;

const DeleteModalOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const DeleteModal = styled.div`
  background: white;
  padding: 36px 32px 28px 32px;
  border-radius: 16px;
  box-shadow: 0 6px 32px rgba(0,0,0,0.18);
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 320px;
`;

const ModalActions = styled.div`
  margin-top: 28px;
  display: flex;
  gap: 16px;
`;

const PaginationWrapper = styled.div`
  display: flex;
  gap: 6px;
  justify-content: center;
  margin: 32px 0 0 0;
  align-items: center;
`;

const PaginationButton = styled.button`
  border: none;
  background: ${({ active }) => (active ? '#ba824f' : '#fff')};
  color: ${({ active }) => (active ? '#fff' : '#ba824f')};
  border-radius: 6px;
  min-width: 32px;
  height: 32px;
  font-weight: ${({ active }) => (active ? 'bold' : 'normal')};
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  box-shadow: ${({ active }) => (active ? '0 0 0 2px #ba824f33' : 'none')};
  &:hover {
    background: #e5d2c0;
    color: #ba824f;
  }
  &:disabled {
    background: #f2f2f2;
    color: #ccc;
    cursor: not-allowed;
  }
`;

const Ellipsis = styled.span`
  padding: 0 8px;
  color: #aaa;
  user-select: none;
`;

// Helper to format numbers using the listing currency (falls back to BRL)
function formatCurrency(value, currencyISO) {
  if (value === null || value === undefined) return '';
  const iso = currencyISO || 'BRL';

  // Aceita tanto formatos "12215,33" quanto "12215.33"
  const raw = String(value).replace(/[^\d,.-]/g, '');
  let numeric;

  if (raw.includes(',')) {
    // Formato brasileiro: 12.215,33 -> 12215.33
    const normalized = raw.replace(/\./g, '').replace(',', '.');
    numeric = Number(normalized);
  } else {
    // Formato internacional ou número puro: 12215.33 -> 12215.33
    numeric = Number(raw);
  }

  if (Number.isNaN(numeric)) return '';
  try {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: iso
    }).format(numeric);
  } catch {
    // Se a moeda não for reconhecida pelo Intl, volta para BRL
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(numeric);
  }
}

// Helper para formatar datas no padrão pt-BR
function formatDateBR(dateLike) {
  if (!dateLike) return '';
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('pt-BR');
}

const LAST_VISITED_LISTING_KEY = 'dashboard_lastVisitedListingId';

const Listings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'inactive', 'highlighted'
  const [toastMessage, setToastMessage] = useState('');
  const [listings, setListings] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [listingToDelete, setListingToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  const [infoModal, setInfoModal] = useState(null); // { title, message }
  const [confirmModal, setConfirmModal] = useState(null); // { title, message, onConfirm }
  const [importModalOpen, setImportModalOpen] = useState(false);

  // Função de filtro combinando busca e status
  const filteredListings = listings.filter(listing => {
    // Filtro de status
    if (statusFilter === 'active' && !listing.active) return false;
    if (statusFilter === 'inactive' && listing.active) return false;
    if (statusFilter === 'highlighted' && !listing.highlighted) return false;
    // Filtro de busca (título ou localização)
    const search = searchTerm.trim().toLowerCase();
    if (!search) return true;
    return (
      (listing.title && listing.title.toLowerCase().includes(search)) ||
      (listing.location && listing.location.toLowerCase().includes(search))
    );
  });

  // Paginação baseada nos filtrados
  const paginatedListings = filteredListings.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const totalPages = Math.ceil(filteredListings.length / itemsPerPage);


  const updateReturnState = (prevId) => {
    if (typeof window === 'undefined') return;
    const state = { ...(window.history.state || {}), returnToListingId: prevId || null };
    window.history.replaceState(state, document.title || '', window.location.pathname + window.location.search);
  };

  const openListingEdit = (listingId) => {
    if (!listingId) return;
    const prevListingId = typeof window !== 'undefined' ? sessionStorage.getItem(LAST_VISITED_LISTING_KEY) : null;
    if (prevListingId && prevListingId !== String(listingId)) {
      updateReturnState(prevListingId);
    } else {
      updateReturnState(null);
    }
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(LAST_VISITED_LISTING_KEY, String(listingId));
    }
    navigate(`/dashboard/edit-listing/${listingId}`);
  };

  useEffect(() => {
    if (location.pathname !== '/dashboard/listings' || navigationType !== 'POP') return;
    if (typeof window === 'undefined') return;
    const returnToListingId = window.history.state?.returnToListingId;
    if (!returnToListingId) return;
    updateReturnState(null);
    sessionStorage.setItem(LAST_VISITED_LISTING_KEY, String(returnToListingId));
    navigate(`/dashboard/edit-listing/${returnToListingId}`, { replace: true });
  }, [location.pathname, navigationType, navigate]);

  const handleOpenDeleteModal = (listing) => {
    setListingToDelete(listing);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setListingToDelete(null);
    setDeleteModalOpen(false);
  };

  const handleDeleteListing = async () => {
    try {
      if (!listingToDelete) return;
      // Corrigido: usar DELETE com Authorization (muitos endpoints exigem AUTH)
      const token = localStorage.getItem('auth_token');
      console.log('[Listings] Tentando deletar imóvel:', listingToDelete.id);
      const response = await fetch(`${import.meta.env.VITE_URL_API}/imoveis/${listingToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'accept': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      console.log('[Listings] Resposta DELETE:', response.status, response.statusText);
      if (!(response.ok || response.status === 204)) {
        const text = await response.text().catch(() => '');
        console.error('[Listings] Erro ao deletar imóvel:', text);
        throw new Error(text || `Falha ${response.status} ao remover anúncio`);
      }
      setListings(prev => prev.filter(l => l.id !== listingToDelete.id));
      setDeleteModalOpen(false);
      setListingToDelete(null);
      // feedback simples opcional
      setToastMessage('Anúncio excluído com sucesso!');
      setTimeout(() => setToastMessage(''), 2500);
    } catch (error) {
      console.error('[Listings] Exceção ao deletar imóvel:', error);
      setDeleteModalOpen(false);
      setListingToDelete(null);
      alert(`Erro ao excluir anúncio: ${error?.message || 'Erro no servidor (500). Contacte o suporte.'}`);
    }
  };

  const handleToggle = async (id, currentStatus) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${import.meta.env.VITE_URL_API}/imoveis/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'accept': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ ativoIMOVEL: currentStatus ? 0 : 1 })
      });
      if (!response.ok) throw new Error('Erro ao atualizar status');
      setListings(prev =>
        prev.map(listing =>
          listing.id === id ? { ...listing, active: !currentStatus } : listing
        )
      );
    } catch (err) {
      // Opcional: mostrar mensagem de erro
    }
  };

  const handleHighlight = async (id, currentHighlight) => {
    try {
      const listing = listings.find(l => l.id === id);
      if (!listing) return;
      const now = new Date();
      const d = listing.contractedHighlight; // registro atrelado ao imóvel (se existir)
      const hasContracted = !!d && Number(d.ativoDESTAQUE) === 1;
      const exp = d?.data_expiracaoDESTAQUE ? new Date(d.data_expiracaoDESTAQUE) : null;
      const isValidContracted = hasContracted && (!exp || exp > now);

      // Busca checkouts ativos de "Destaque" do usuário
      let hasActiveCheckout = false;
      let planoDESTAQUE;
      let checkoutExpireAt; // data de validade do checkout
      try {
        const token = localStorage.getItem('auth_token');
        const chkRes = await fetch(`${import.meta.env.VITE_URL_API}/checkouts`, {
          headers: {
            'accept': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          }
        });
        const chkData = await chkRes.json();
        const nowMs = Date.now();
        const norm = s => (s || '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        const destaqueCheckout = (Array.isArray(chkData) ? chkData : (chkData?.checkouts || [])).find(c => {
          const nomeOk = norm(c.nomeCHECKOUT).includes('destaque');
          const aq = new Date(c.dataCHECKOUT);
          const val = new Date(aq);
          val.setDate(val.getDate() + Number(c.periodoCHECKOUT || 0));
          const valido = val.getTime() > nowMs;
          return nomeOk && Number(c.ativoCHECKOUT) === 1 && valido;
        });
        if (destaqueCheckout?.codigoCHECKOUT) {
          hasActiveCheckout = true;
          planoDESTAQUE = destaqueCheckout.codigoCHECKOUT;
          const aq = new Date(destaqueCheckout.dataCHECKOUT);
          const val = new Date(aq);
          val.setDate(val.getDate() + Number(destaqueCheckout.periodoCHECKOUT || 0));
          checkoutExpireAt = val;
        }
      } catch {
        // Silently ignore checkout fetch errors
      }

      // Se já está destacado e ainda válido (pelo registro do imóvel), não pode remover antes do prazo
      if (currentHighlight && isValidContracted) {
        setInfoModal({ title: 'Destaque ativo', message: 'Este anúncio está em destaque ativo. Não é possível remover antes do fim da validade.' });
        return;
      }

      // Aplicar destaque se: não está destacado e (registro válido OU checkout ativo) e não usado
      const used = Number(d?.pagoDESTAQUE) === 1;
      const isValid = isValidContracted || hasActiveCheckout;
      if (!currentHighlight && isValid && !used) {
        const token = localStorage.getItem('auth_token');
        const userRaw = localStorage.getItem('user');
        let usuarioId = undefined;
        try { usuarioId = JSON.parse(userRaw)?.codigo || JSON.parse(userRaw)?.codigoUSUARIO || JSON.parse(userRaw)?.id; } catch {
          // Silently ignore user parsing errors
        }

        const expireAtISO = (exp && exp > now) ? exp.toISOString() : (checkoutExpireAt ? checkoutExpireAt.toISOString() : new Date(now.getTime() + 30*24*60*60*1000).toISOString());

        const body = {
          imovelDESTAQUE: id,
          ...(usuarioId ? { usuarioDESTAQUE: usuarioId } : {}),
          quantidadeDESTAQUE: 1,
          data_aquisicaoDESTAQUE: new Date().toISOString(),
          data_expiracaoDESTAQUE: expireAtISO,
          pagoDESTAQUE: 1,
          valorDESTAQUE: d?.valorDESTAQUE ?? 0,
          ativoDESTAQUE: 1,
          ...(planoDESTAQUE ? { planoDESTAQUE } : {})
        };
        const postRes = await fetch(`${import.meta.env.VITE_URL_API}/destaques`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'accept': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          body: JSON.stringify(body)
        });
        if (!postRes.ok) {
          const t = await postRes.text().catch(() => '');
          throw new Error(t || 'Erro ao registrar uso do destaque');
        }

        // Ativa o destaque no imóvel
        const putRes = await fetch(`${import.meta.env.VITE_URL_API}/imoveis/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'accept': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          body: JSON.stringify({ destaqueIMOVEL: 1 })
        });
        if (!putRes.ok) throw new Error('Erro ao ativar destaque no anúncio');

        setListings(prev => prev.map(l => l.id === id ? {
          ...l,
          highlighted: true,
          contractedHighlight: {
            ...(l.contractedHighlight || {}),
            pagoDESTAQUE: 1,
            ativoDESTAQUE: 1,
            data_expiracaoDESTAQUE: expireAtISO
          }
        } : l));
        setToastMessage('Anúncio colocado em destaque com sucesso!');
        setTimeout(() => setToastMessage(''), 2500);
        return;
      }

      // Se não há destaque contratado válido em nenhum dos cenários, direciona para aquisição
      if (!currentHighlight && !isValid) {
        setConfirmModal({
          title: 'Destaque não contratado',
          message: 'Você não possui destaque contratado ou está expirado. Deseja adquirir um destaque agora?',
          onConfirm: () => {
            setConfirmModal(null);
            window.location.href = '/dashboard/packages?tab=Destaque';
          }
        });
        return;
      }

      // Caso caia aqui e não seja válido (registro expirado e sem checkout), permitir remover
      if (currentHighlight && !isValid) {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${import.meta.env.VITE_URL_API}/imoveis/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'accept': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          body: JSON.stringify({ destaqueIMOVEL: 0 })
        });
        if (!response.ok) throw new Error('Erro ao remover destaque');
        setListings(prev => prev.map(l => l.id === id ? { ...l, highlighted: false } : l));
      }
    } catch (err) {
      let title = 'Erro';
      let msg = err?.message || 'Não foi possível atualizar o destaque.';
      try {
        const parsed = JSON.parse(msg);
        if (parsed?.error) {
          if (parsed.error === 'Limite de destaques atingido') {
            title = 'Limite de destaques';
            msg = `Você atingiu o limite de destaques do seu plano.\n\n• Limite: ${parsed.limite}\n• Utilizados: ${parsed.utilizados}\n• Disponíveis: ${parsed.disponiveis}`;
            setInfoModal({ title, message: msg });
            
            // Redireciona automaticamente após 6 segundos
            setTimeout(() => {
              window.location.href = '/dashboard/packages?tab=Destaque';
            }, 6000);
            return;
          } else {
            msg = parsed.error;
          }
        }
      } catch {
        // mantém a mensagem original se não for JSON
      }
      setInfoModal({ title, message: msg });
    }
  };

  const fetchListings = React.useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const url = `${import.meta.env.VITE_URL_API}/imoveis/me`;
      const res = await fetch(url, {
        headers: {
          'accept': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      if (!res.ok) {
        console.error('Erro na API /imoveis/me:', res.status, res.statusText);
        setListings([]);
        return;
      }
      const data = await res.json();
      console.log('DEBUG /imoveis/me raw data:', data);
      const now = new Date();
      // Log de debug para verificar os dados do backend
      console.group('📥 DEBUG: Dados recebidos do backend');
      const imoveisArray = data?.imoveis || [];
      const firstImovel = imoveisArray[0];
      if (firstImovel) {
        console.log('Primeiro imóvel:');
        console.log('  tituloIMOVEL:', firstImovel.tituloIMOVEL);
        console.log('  valorIMOVEL:', firstImovel.valorIMOVEL);
        console.log('  moedaIMOVEL:', firstImovel.moedaIMOVEL);
        console.log('  casadebanhoIMOVEL:', firstImovel.casadebanhoIMOVEL);
        console.log('  banheirosIMOVEL:', firstImovel.banheirosIMOVEL);
        console.log('  quartosIMOVEL:', firstImovel.quartosIMOVEL);
        console.log('  vagasgaragemIMOVEL:', firstImovel.vagasgaragemIMOVEL);
        console.log('  metragemIMOVEL:', firstImovel.metragemIMOVEL);
        console.log('  tipoIMOVEL:', firstImovel.tipoIMOVEL);
        console.log('  Objeto completo:', firstImovel);
      } else {
        console.log('Nenhum imóvel encontrado');
      }
      console.groupEnd();
      setListings(imoveisArray.map(imovel => {
        const d = imovel.destaque || null;
        const exp = d?.data_expiracaoDESTAQUE ? new Date(d.data_expiracaoDESTAQUE) : null;
        const contractedValid = !!d && Number(d.ativoDESTAQUE) === 1 && (!exp || exp > now);
        return ({
          id: imovel.codigoIMOVEL,
          image: imovel.fotos && imovel.fotos.length > 0 ? imovel.fotos[0].urlFOTO : '',
          title: imovel.tituloIMOVEL,
          location: `${imovel.enderecoIMOVEL || ''}${imovel.cityIMOVEL ? ', ' + imovel.cityIMOVEL : ''}${imovel.stateIMOVEL ? ', ' + imovel.stateIMOVEL : ''}`,
          bedrooms: imovel.quartosIMOVEL,
          bathrooms: imovel.casadebanhoIMOVEL ?? imovel.banheirosIMOVEL,
          area: imovel.metragemIMOVEL,
          areaUnit: (() => {
            const rawVal = imovel.tipometragemIMOVEL;
            if (rawVal === 2 || rawVal === '2') return 'ft²';
            if (rawVal === 1 || rawVal === '1') return 'm²';
            const raw = String(rawVal || '').toLowerCase();
            if (raw.includes('ft')) return 'ft²';
            if (raw.includes('m')) return 'm²';
            return 'm²';
          })(),
          parking: imovel.vagasgaragemIMOVEL,
          tipoImovel: imovel.tipoIMOVEL,
          // Sempre exibe o valor em euro na tela de Meus Anúncios,
          // independentemente da moeda original do imóvel.
          price: imovel.valorIMOVEL !== null && imovel.valorIMOVEL !== undefined
            ? formatCurrency(imovel.valorIMOVEL, 'EUR')
            : '',
          active: imovel.ativoIMOVEL === 1 || imovel.ativoIMOVEL === '1',
          // Selo de destaque agora depende exclusivamente do backend
          isDestaque: !!imovel.isDestaque,
          highlighted: imovel.destaqueIMOVEL === 1 || imovel.destaqueIMOVEL === '1',
          contractedHighlight: d,
          highlightContractValid: contractedValid,
          originId: imovel.tipoorigemIMOVEL || 0,
        });
      }));
    } catch (err) {
      setListings([]);
    }
  }, []);

  React.useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  async function handleDuplicate(id) {
    try {
      const token = localStorage.getItem('auth_token');
      // Verifica se há vaga disponível de imóveis nos planos ativos (exceto Destaque)
      try {
        const chkRes = await fetch(`${import.meta.env.VITE_URL_API}/checkouts`, {
          headers: {
            'accept': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          }
        });
        const chkData = await chkRes.json();
        const now = new Date();
        const norm = s => (s || '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        const activePlans = (Array.isArray(chkData) ? chkData : (chkData?.checkouts || [])).filter(c => {
          const aq = new Date(c.dataCHECKOUT);
          const val = new Date(aq);
          val.setDate(val.getDate() + Number(c.periodoCHECKOUT || 0));
          const valido = val > now;
          const isActive = Number(c.ativoCHECKOUT) === 1;
          const isDestaque = norm(c.nomeCHECKOUT).includes('destaque');
          return isActive && valido && !isDestaque;
        });
        const totalPermitido = activePlans.reduce((sum, c) => sum + Number(c?.plano?.quantidadePLANO || c?.quantidadeCHECKOUT || 0), 0);
        const usados = listings.length; // quantidade de anúncios do usuário
        const restante = totalPermitido - usados;
        if (restante <= 0) {
          setConfirmModal({
            title: 'Limite de anúncios atingido',
            message: 'Você atingiu o limite de imóveis do seu(s) plano(s). Deseja adquirir mais anúncios agora?',
            onConfirm: () => {
              setConfirmModal(null);
              window.location.href = '/dashboard/packages?tab=Particular';
            }
          });
          return;
        }
      } catch {
        // Silently ignore checkout fetch errors
      }
      const res = await fetch(`${import.meta.env.VITE_URL_API}/imoveis/${id}/duplicate`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      if (res.status !== 201 && !res.ok) {
        const t = await res.text().catch(() => '');
        throw new Error(t || 'Falha ao duplicar anúncio');
      }
      const dup = await res.json();
      setInfoModal({ title: 'Duplicado', message: `Anúncio duplicado como #${dup.codigoIMOVEL}.` });
      await fetchListings();
    } catch (e) {
      setInfoModal({ title: 'Erro ao duplicar', message: e?.message || 'Não foi possível duplicar o anúncio.' });
    }
  }

  return (
    <PageContainer>
      <WelcomeHeader>
        <h1>Meus Anúncios</h1>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <ImportButton onClick={() => setImportModalOpen(true)}>
            <FaLink />
            Importar anúncio
          </ImportButton>
          <AddButton onClick={() => navigate('/dashboard/newlisting')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Novo Anúncio
          </AddButton>
        </div>
      </WelcomeHeader>

      <FiltersContainer>
        <SearchInput
          type="text"
          placeholder="Buscar por título ou localização..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1); // volta para página 1 ao buscar
          }}
          style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid #eee', marginBottom: 16 }}
        />
        <FilterSelect
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
          }}
          style={{ minWidth: 180, padding: 8, borderRadius: 8, border: '1px solid #eee', marginLeft: 16 }}
        >
          <option value="all">Todos os Status</option>
          <option value="active">Ativos</option>
          <option value="inactive">Inativos</option>
          <option value="highlighted">Destaques</option>
        </FilterSelect>
      </FiltersContainer>

      <ListingsGrid>
        {paginatedListings.length > 0 ? paginatedListings.map(listing => (
          <ListingCard key={listing.id}>
            <ListingImage image={listing.image}>
              <TagsOverlay>
                {listing.tipoImovel && (
                  <CategoryTag
                    $category={Number(listing.tipoImovel) === 1 ? 'buy' : Number(listing.tipoImovel) === 2 ? 'rent' : Number(listing.tipoImovel) === 3 ? 'vacation' : ''}
                  >
                    {Number(listing.tipoImovel) === 1 ? t('propertyCard.category.buy') : Number(listing.tipoImovel) === 2 ? t('propertyCard.category.rent') : Number(listing.tipoImovel) === 3 ? t('propertyCard.category.vacation') : ''}
                  </CategoryTag>
                )}
                {listing.isDestaque && (
                  <HighlightBadge>
                    <svg viewBox="0 0 24 24">
                      <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                    </svg>
                    Destaque
                  </HighlightBadge>
                )}
                {listing.contractedHighlight?.data_expiracaoDESTAQUE && (
                  <ValidityTag>
                    Validade: {formatDateBR(listing.contractedHighlight.data_expiracaoDESTAQUE)}
                  </ValidityTag>
                )}
                <ListingStatus $active={listing.active}>
                  {listing.active ? t('propertyCard.status.active') : t('propertyCard.status.inactive')}
                </ListingStatus>
              </TagsOverlay>
            </ListingImage>
            <ListingContent>
              <ListingTitle>
                {listing.title}
                {listing.originId > 0 && (
                  <CopyTag>
                    Cópia de #{listing.originId}
                    <button
                      onClick={(e) => { e.stopPropagation(); openListingEdit(listing.originId); }}
                      style={{ border: 'none', background: 'transparent', color: '#2f4aa0', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                    >abrir</button>
                  </CopyTag>
                )}
              </ListingTitle>
              <ListingLocation>{listing.location}</ListingLocation>
              <ListingStats>
                {listing.bedrooms != null && listing.bedrooms !== '' && (
                  <div className="stat">
                    <span>{listing.bedrooms}</span>
                    <small>{t('propertyCard.bedrooms')}</small>
                  </div>
                )}
                {listing.bathrooms != null && listing.bathrooms !== '' && (
                  <div className="stat">
                    <span>{listing.bathrooms}</span>
                    <small>{Number(listing.bathrooms) === 1 ? t('propertyCard.bathrooms.singular') : t('propertyCard.bathrooms.plural')}</small>
                  </div>
                )}
                {listing.area != null && listing.area !== '' && (
                  <div className="stat">
                    <span>{listing.area}{listing.areaUnit || 'm²'}</span>
                    <small>{t('propertyCard.area')}</small>
                  </div>
                )}
                {listing.parking != null && listing.parking !== '' && (
                  <div className="stat">
                    <span>{listing.parking}</span>
                    <small>{Number(listing.parking) === 1 ? t('propertyCard.parking.singular') : t('propertyCard.parking.plural')}</small>
                  </div>
                )}
              </ListingStats>
              <ListingFooter>
                <ListingPrice>{listing.price}</ListingPrice>
                <ListingActions>
                  <ToggleSwitch title={listing.active ? 'Desativar anúncio' : 'Ativar anúncio'}>
                    <input
                      type="checkbox"
                      checked={listing.active}
                      onChange={() => handleToggle(listing.id, listing.active)}
                    />
                    <span className="slider" />
                  </ToggleSwitch>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <IconButton className="primary" title="Editar anúncio" onClick={() => openListingEdit(listing.id)}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 20h9"/>
                        <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4 12.5-12.5z"/>
                      </svg>
                    </IconButton>
                    <IconButton className="primary" title="Duplicar anúncio" onClick={() => handleDuplicate(listing.id)}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                        <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                      </svg>
                    </IconButton>
                    <IconButton className="highlight" $active={listing.highlighted} title={listing.highlighted ? 'Remover destaque' : 'Colocar em destaque'} onClick={() => handleHighlight(listing.id, listing.highlighted)}>
                      <svg viewBox="0 0 24 24" fill={listing.highlighted ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                      </svg>
                    </IconButton>
                    <IconButton className="warn" title="Excluir anúncio" onClick={() => handleOpenDeleteModal(listing)}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"/>
                        <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                        <path d="M10 11v6M14 11v6"/>
                        <path d="M9 6V4a2 2 0 012-2h2a2 2 0 012 2v2"/>
                      </svg>
                    </IconButton>
                  </div>
                </ListingActions>
              </ListingFooter>
            </ListingContent>
          </ListingCard>
        )) : (
          <div style={{textAlign: 'center', color: '#aaa', padding: 32}}>Nenhum anúncio encontrado.</div>
        )}
      </ListingsGrid>
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', margin: '24px 0', gap: 8 }}>
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            style={{ padding: '6px 16px', borderRadius: 6, border: '1px solid #eee', background: '#fff', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
          >Anterior</button>
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentPage(i + 1)}
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: '1px solid #eee',
                background: currentPage === i + 1 ? '#34574A' : '#fff',
                color: currentPage === i + 1 ? '#fff' : '#34574A',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >{i + 1}</button>
          ))}
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            style={{ padding: '6px 16px', borderRadius: 6, border: '1px solid #eee', background: '#fff', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
          >Próxima</button>
        </div>
      )}
      {deleteModalOpen && (
        <DeleteModalOverlay>
          <DeleteModal>
            <h3 style={{marginBottom: 12}}>Excluir anúncio</h3>
            <p style={{marginBottom: 0}}>Tem certeza que deseja excluir este anúncio?</p>
            <ModalActions>
              <ActionButton onClick={handleCloseDeleteModal}>Cancelar</ActionButton>
              <ActionButton highlight onClick={handleDeleteListing}>Excluir</ActionButton>
            </ModalActions>
          </DeleteModal>
        </DeleteModalOverlay>
      )}

      {infoModal && (
        <InfoModal
          title={infoModal.title}
          message={infoModal.message}
          onClose={() => setInfoModal(null)}
        />
      )}

      {confirmModal && (
        <ConfirmModal
          title={confirmModal.title}
          message={confirmModal.message}
          confirmText={confirmModal.confirmText || 'Confirmar'}
          cancelText={confirmModal.cancelText || 'Cancelar'}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal(null)}
        />
      )}

      {importModalOpen && (
        <ImportListingModal
          onClose={() => setImportModalOpen(false)}
          onImported={() => fetchListings()}
        />
      )}

      {toastMessage && (
        <Toast>
          <svg viewBox="0 0 24 24">
            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
          </svg>
          {toastMessage}
        </Toast>
      )}
    </PageContainer>
  );
};

export default Listings;